/**
 * Sistema de caché para archivos Excel usando IndexedDB
 * Permite almacenar y recuperar archivos localmente para uso offline
 */

const ExcelCache = {
    db: null,
    isInitialized: false,

    /**
     * Inicializa la base de datos IndexedDB
     */
    async init() {
        if (this.isInitialized) return true;

        const config = window.LoaderConfig?.cache || {
            dbName: 'ProblemasDB',
            storeName: 'excelFiles',
            version: 1
        };

        return new Promise((resolve) => {
            if (typeof indexedDB === 'undefined') {
                console.warn('⚠️ IndexedDB no está disponible en este entorno');
                resolve(false);
                return;
            }
            try {
                const request = indexedDB.open(config.dbName, config.version);

                request.onerror = () => {
                    console.warn('⚠️ Error o restricción abriendo IndexedDB:', request.error);
                    resolve(false);
                };

                request.onsuccess = () => {
                    this.db = request.result;
                    this.isInitialized = true;
                    console.log('✅ IndexedDB inicializado');
                    resolve(true);
                };

                request.onupgradeneeded = (event) => {
                    const db = event.target.result;
                    if (!db.objectStoreNames.contains(config.storeName)) {
                        const objectStore = db.createObjectStore(config.storeName, { keyPath: 'fileName' });
                        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
                        objectStore.createIndex('version', 'version', { unique: false });
                        console.log('📦 Object store creado');
                    }
                };
            } catch (e) {
                console.warn('⚠️ Excepción al intentar usar IndexedDB:', e);
                resolve(false);
            }
        });
    },

    /**
     * Guarda un archivo Excel en el caché
     * @param {string} fileName - Nombre del archivo
     * @param {ArrayBuffer} data - Datos del archivo
     * @param {Object} metadata - Metadatos adicionales (ETag, Last-Modified, etc.)
     */
    async save(fileName, data, metadata = {}) {
        try {
            await this.init();
            if (!this.db) {
                console.warn('⚠️ IndexedDB no disponible, no se guardó en caché');
                return false;
            }

            const config = window.LoaderConfig?.cache || { storeName: 'excelFiles' };
            const transaction = this.db.transaction([config.storeName], 'readwrite');
            const objectStore = transaction.objectStore(config.storeName);

            const record = {
                fileName: fileName,
                data: data,
                timestamp: Date.now(),
                size: data.byteLength,
                etag: metadata.etag || null,
                lastModified: metadata.lastModified || null,
                version: metadata.version || '1.0'
            };

            const request = objectStore.put(record);

            return new Promise((resolve, reject) => {
                request.onsuccess = () => {
                    console.log(`💾 Archivo guardado en caché: ${fileName} (${(data.byteLength / 1024).toFixed(2)} KB)`);
                    resolve(true);
                };

                request.onerror = () => {
                    console.error('❌ Error guardando en caché:', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            console.error('❌ Error en ExcelCache.save:', error);
            return false;
        }
    },

    /**
     * Recupera un archivo del caché
     * @param {string} fileName - Nombre del archivo
     * @returns {Object|null} - Objeto con data y metadata, o null si no existe
     */
    async get(fileName) {
        try {
            await this.init();
            if (!this.db) {
                return null;
            }

            const config = window.LoaderConfig?.cache || {
                storeName: 'excelFiles',
                maxAge: 7 * 24 * 60 * 60 * 1000
            };
            const transaction = this.db.transaction([config.storeName], 'readonly');
            const objectStore = transaction.objectStore(config.storeName);
            const request = objectStore.get(fileName);

            return new Promise((resolve, reject) => {
                request.onsuccess = () => {
                    const record = request.result;

                    if (!record) {
                        console.log(`📭 No hay caché para: ${fileName}`);
                        resolve(null);
                        return;
                    }

                    // Verificar edad del caché
                    const age = Date.now() - record.timestamp;
                    const isExpired = age > config.maxAge;

                    if (isExpired) {
                        console.warn(`⏰ Caché expirado para ${fileName} (${Math.floor(age / (24 * 60 * 60 * 1000))} días)`);
                    }

                    console.log(`📦 Caché encontrado: ${fileName} (${(record.size / 1024).toFixed(2)} KB, ${Math.floor(age / (60 * 60 * 1000))}h de antigüedad)`);

                    resolve({
                        data: record.data,
                        metadata: {
                            timestamp: record.timestamp,
                            size: record.size,
                            etag: record.etag,
                            lastModified: record.lastModified,
                            version: record.version,
                            isExpired: isExpired,
                            age: age
                        }
                    });
                };

                request.onerror = () => {
                    console.error('❌ Error recuperando del caché:', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            console.error('❌ Error en ExcelCache.get:', error);
            return null;
        }
    },

    /**
     * Verifica si hay una versión más nueva disponible
     * @param {string} fileName - Nombre del archivo
     * @param {string} url - URL del archivo
     * @returns {boolean} - true si hay una versión más nueva
     */
    async hasNewerVersion(fileName, url) {
        try {
            const cached = await this.get(fileName);
            if (!cached) return true; // No hay caché, considerar como nueva versión

            // Hacer HEAD request para obtener metadatos
            const response = await fetch(url, {
                method: 'HEAD',
                cache: 'no-cache'
            });

            if (!response.ok) return false; // Si falla, usar caché

            const serverETag = response.headers.get('ETag');
            const serverLastModified = response.headers.get('Last-Modified');

            // Comparar ETags si están disponibles
            if (serverETag && cached.metadata.etag) {
                return serverETag !== cached.metadata.etag;
            }

            // Comparar Last-Modified si está disponible
            if (serverLastModified && cached.metadata.lastModified) {
                return new Date(serverLastModified) > new Date(cached.metadata.lastModified);
            }

            // Si no hay metadatos para comparar, considerar como nueva versión
            return true;
        } catch (error) {
            console.warn('⚠️ Error verificando versión:', error);
            return false; // En caso de error, usar caché
        }
    },

    /**
     * Limpia el caché completo
     */
    async clear() {
        try {
            await this.init();

            const config = window.LoaderConfig?.cache || { storeName: 'excelFiles' };
            const transaction = this.db.transaction([config.storeName], 'readwrite');
            const objectStore = transaction.objectStore(config.storeName);
            const request = objectStore.clear();

            return new Promise((resolve, reject) => {
                request.onsuccess = () => {
                    console.log('🗑️ Caché limpiado completamente');
                    resolve(true);
                };

                request.onerror = () => {
                    console.error('❌ Error limpiando caché:', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            console.error('❌ Error en ExcelCache.clear:', error);
            return false;
        }
    },

    /**
     * Elimina un archivo específico del caché
     * @param {string} fileName - Nombre del archivo a eliminar
     */
    async delete(fileName) {
        try {
            await this.init();

            const config = window.LoaderConfig?.cache || { storeName: 'excelFiles' };
            const transaction = this.db.transaction([config.storeName], 'readwrite');
            const objectStore = transaction.objectStore(config.storeName);
            const request = objectStore.delete(fileName);

            return new Promise((resolve, reject) => {
                request.onsuccess = () => {
                    console.log(`🗑️ Archivo eliminado del caché: ${fileName}`);
                    resolve(true);
                };

                request.onerror = () => {
                    console.error('❌ Error eliminando del caché:', request.error);
                    reject(request.error);
                };
            });
        } catch (error) {
            console.error('❌ Error en ExcelCache.delete:', error);
            return false;
        }
    },

    /**
     * Obtiene estadísticas del caché
     */
    async getStats() {
        try {
            await this.init();

            const config = window.LoaderConfig?.cache || { storeName: 'excelFiles' };
            const transaction = this.db.transaction([config.storeName], 'readonly');
            const objectStore = transaction.objectStore(config.storeName);
            const request = objectStore.getAll();

            return new Promise((resolve, reject) => {
                request.onsuccess = () => {
                    const records = request.result;
                    const totalSize = records.reduce((sum, r) => sum + r.size, 0);
                    const oldestTimestamp = Math.min(...records.map(r => r.timestamp));

                    resolve({
                        count: records.length,
                        totalSize: totalSize,
                        totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2),
                        oldestCache: oldestTimestamp ? new Date(oldestTimestamp) : null,
                        files: records.map(r => ({
                            name: r.fileName,
                            size: r.size,
                            timestamp: new Date(r.timestamp)
                        }))
                    });
                };

                request.onerror = () => {
                    reject(request.error);
                };
            });
        } catch (error) {
            console.error('❌ Error obteniendo estadísticas:', error);
            return null;
        }
    }
};

// Exponer globalmente
window.ExcelCache = ExcelCache;

console.log('💾 ExcelCache cargado');
